import { useEffect, useState } from 'react';
import ShootDayList from '@/components/creative/ShootDayList';
import { ShootBoards, OnSetCard } from '@/components/creative/BoardCard';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, MapPin, Clock, Plus, X, Trash2, Check, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import ShootInvitesCard from '@/components/meetings/ShootInvitesCard';
import {
  fetchShootSession, patchShootSession, deleteShootSession, addCreativesToShoot, removeCreativeFromShoot, fetchShootCrewCandidates, decideShoot, type ShootCreative,
} from '@/lib/queries';
import PromptDialog from '@/components/ui/PromptDialog';
import { useCan } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';

const SCRIPT: Record<string, { label: string; cls: string }> = {
  draft:             { label: 'script not sent',    cls: 'bg-slate-700/40 text-slate-300' },
  sent:              { label: 'waiting for doctor', cls: 'bg-amber-500/15 text-amber-300' },
  approved:          { label: 'script approved',    cls: 'bg-emerald-500/15 text-emerald-300' },
  changes_requested: { label: 'changes requested',  cls: 'bg-red-500/15 text-red-300' },
};

function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

export default function ShootDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const canPlan = useCan('shoots.plan');
  const q = useQuery({ queryKey: ['shoots', id], queryFn: () => fetchShootSession(id), enabled: !!id });
  const s = q.data;

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(''); const [when, setWhen] = useState(''); const [where, setWhere] = useState(''); const [notes, setNotes] = useState(''); const [status, setStatus] = useState('planned');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [declining, setDeclining] = useState(false);
  const decide = useMutation({
    mutationFn: ({ decision, note }: { decision: 'approved' | 'declined'; note?: string }) => decideShoot(id, decision, note),
    onSuccess: (r) => { toast.success(r.status === 'scheduled' ? 'Approved. The crew is told.' : 'Declined. The planner is told.'); setDeclining(false); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['shoots'] }); qc.invalidateQueries({ queryKey: ['shoot'] }); };
  const [crewEdit, setCrewEdit] = useState<string[] | null>(null);
  const candidates = useQuery({ queryKey: ['shoot-crew', s?.client_id], queryFn: () => fetchShootCrewCandidates(s!.client_id), enabled: canPlan && !!s?.client_id });
  const saveCrew = useMutation({
    mutationFn: () => patchShootSession(id, { crew_user_ids: crewEdit ?? [] }),
    onSuccess: () => { toast.success('Crew updated'); setCrewEdit(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  useEffect(() => { if (s) { setTitle(s.title); setWhen(toLocalInput(s.shoot_at)); setWhere(s.location ?? ''); setNotes(s.notes ?? ''); setStatus(s.status); } }, [s]);

  const save = useMutation({
    mutationFn: () => patchShootSession(id, { title, shoot_at: new Date(when).toISOString(), location: where, notes, ...(status === 'done' || status === 'cancelled' ? { status } : {}) }),
    onSuccess: () => { toast.success('Shoot updated'); setEditing(false); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const add = useMutation({
    mutationFn: () => addCreativesToShoot(id, Array.from(picked)),
    onSuccess: (r) => { toast.success(`${r.creative_count} creative${r.creative_count === 1 ? '' : 's'} on this shoot`); setPicked(new Set()); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (cid: string) => removeCreativeFromShoot(id, cid),
    onSuccess: () => { toast.success('Taken off the shoot'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: () => deleteShootSession(id),
    onSuccess: () => { toast.success('Shoot deleted'); invalidate(); nav('/shoots'); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!id) return <Navigate to="/shoots" replace />;
  if (q.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!s) return <Navigate to="/shoots" replace />;

  const whenLabel = new Date(s.shoot_at).toLocaleString('en-AE', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  const toggle = (cid: string) => setPicked((p) => { const n = new Set(p); if (n.has(cid)) n.delete(cid); else n.add(cid); return n; });

  return (
    <div>
      <Link to="/shoots" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3"><ArrowLeft size={12} /> All shoots</Link>
      {(s.status === 'proposed' || s.status === 'declined') && (
        <Card className={cn('mb-4', s.status === 'declined' ? 'border-red-500/30' : 'border-amber-500/30')} data-testid="shoot-decision">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', s.status === 'declined' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300')}>{s.status}</span>
            <span className="text-sm text-muted-strong">
              {s.status === 'declined'
                ? <>Declined{s.decline_note ? <>: <span className="text-muted">"{s.decline_note}"</span></> : null}. Change the date or the crew and it is proposed again.</>
                : <>Proposed by {s.proposed_by_name ?? 'the planner'}{s.approver_names.length ? <>, waiting for <span className="text-muted-strong">{s.approver_names.join(', ')}</span></> : ', waiting for the shoot approver'}.</>}
            </span>
            {s.can_decide && (
              <div className="ml-auto flex gap-2">
                <Button size="sm" icon={<Check size={12} />} loading={decide.isPending} onClick={() => decide.mutate({ decision: 'approved' })} data-testid="shoot-approve">Approve</Button>
                <Button size="sm" variant="secondary" icon={<X size={12} />} disabled={decide.isPending} onClick={() => setDeclining(true)} data-testid="shoot-decline">Decline</Button>
              </div>
            )}
          </div>
        </Card>
      )}
      <PageHeader title={s.title} subtitle={`${s.client_name} · ${s.status === 'planned' ? 'scheduled' : s.status}${s.approved_by_name ? ` · approved by ${s.approved_by_name}` : ''}`}
        actions={canPlan && !editing ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
            <Button size="sm" variant="secondary" icon={<Trash2 size={12} />} onClick={() => setConfirmDelete(true)}>Delete</Button>
          </>
        ) : undefined} />

      <ShootInvitesCard s={s} canPlan={canPlan} />

      <Card className="mb-4">
        {!editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><Clock size={10} /> When</div><div className="text-muted-strong">{whenLabel}</div></div>
            <div className="rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><MapPin size={10} /> Where</div><div className="text-muted-strong">{s.location || 'To be confirmed'}</div></div>
            <div className="rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted">Doctors</div><div className="text-muted-strong">{s.doctors.length ? s.doctors.join(', ') : 'None picked yet'}</div></div>
            {s.notes && <div className="sm:col-span-3 rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted">Notes for the crew</div><div className="text-muted-strong whitespace-pre-wrap">{s.notes}</div></div>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <Input label="Date and time" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              <Input label="Location" value={where} onChange={(e) => setWhere(e.target.value)} />
              <Input label="Length (hours)" type="number" min={0.5} max={16} step={0.5} value={String((s.duration_minutes || 240) / 60)} onChange={(e) => patchShootSession(id, { duration_minutes: Math.round(Number(e.target.value) * 60) }).then(invalidate).catch((err) => toast.error(errMsg(err)))} />
              <Select label="Status" value={status === 'planned' ? 'scheduled' : status} onChange={setStatus} options={[{ value: 'proposed', label: 'Proposed', disabled: true }, { value: 'scheduled', label: 'Scheduled', disabled: true }, { value: 'done', label: 'Done' }, { value: 'cancelled', label: 'Cancelled' }]} />
              <div className="sm:col-span-2"><Input label="Notes for the crew" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" icon={<Check size={12} />} onClick={() => save.mutate()} disabled={!when || save.isPending}>Save</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
            <p className="text-[11px] text-muted">Moving the date or place updates every creative on this shoot and tells their crews. A new date, length or videographer on a scheduled shoot goes back to the approver.</p>
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><Video size={14} /> Crew responsible</h3>
          {canPlan && crewEdit === null && <Button size="sm" variant="secondary" onClick={() => setCrewEdit(s.crew_user_ids)}>Change crew</Button>}
        </div>
        {crewEdit === null ? (
          <div className="text-sm text-muted-strong">{s.crew.length ? s.crew.map((m) => `${m.name} (${m.designations.join(', ') || 'crew'})`).join(', ') : <span className="text-muted text-xs">No crew assigned yet. The videographer on duty sees this shoot and every creative on it.</span>}</div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {(candidates.data ?? []).map((u) => (
                <label key={u.id} className={cn('inline-flex items-center gap-2 text-xs px-3 py-1.5 min-h-[36px] rounded-full border cursor-pointer', crewEdit.includes(u.id) ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted')}>
                  <input type="checkbox" className="accent-accent-400" checked={crewEdit.includes(u.id)} onChange={() => setCrewEdit((c) => (c ?? []).includes(u.id) ? (c ?? []).filter((x) => x !== u.id) : [...(c ?? []), u.id])} />
                  {u.display_name}<span className="text-[10px] opacity-70">{u.designations.join(', ')}{u.on_client ? '' : ' · not on this clinic yet'}</span>
                </label>
              ))}
              {(candidates.data ?? []).length === 0 && <span className="text-xs text-muted">No videographer or photographer on the team yet.</span>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" icon={<Check size={12} />} onClick={() => saveCrew.mutate()} disabled={saveCrew.isPending}>Save crew</Button>
              <Button size="sm" variant="secondary" onClick={() => setCrewEdit(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><Camera size={14} /> Creatives on this shoot</h3>
          <span className="text-xs text-muted">{s.creative_count}</span>
        </div>
        {s.creatives.length === 0 && <p className="text-xs text-muted">Nothing on this shoot yet. Add approved creatives below.</p>}
        {/* The day is worked doctor by doctor, so the list is grouped that way, with the
            script, the filmed mark and the on-set correction on each piece. */}
        <ShootDayList shootId={s.id} creatives={s.creatives} canRemove={canPlan}
          onRemove={(id) => remove.mutate(id)} />
      </Card>

      <ShootBoards shootId={s.id} />
      <OnSetCard shoot={s} />

      {canPlan && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><Plus size={14} /> Add creatives</h3>
            <Button size="sm" onClick={() => add.mutate()} disabled={picked.size === 0 || add.isPending} icon={<Plus size={12} />}>Add {picked.size || ''} to this shoot</Button>
          </div>
          <p className="text-xs text-muted mb-3">Only creatives of {s.client_name} whose script the doctor has approved can join. The rest are listed below with what is missing.</p>
          {s.eligible.length === 0 && <p className="text-xs text-muted mb-3">No approved creative is waiting for a shoot.</p>}
          <div className="divide-y divide-slate-800/60">
            {s.eligible.map((c) => (
              <label key={c.id} className="flex items-center gap-3 py-2 cursor-pointer">
                <input type="checkbox" className="accent-accent-400 w-4 h-4" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                <div className="flex-1 min-w-0"><Row c={c} /></div>
              </label>
            ))}
          </div>
          {s.not_ready.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Not ready yet</div>
              <div className="divide-y divide-slate-800/60 opacity-80">
                {s.not_ready.map((c) => <Row key={c.id} c={c} reason={c.reason ?? undefined} />)}
              </div>
            </div>
          )}
        </Card>
      )}

      <PromptDialog mentions open={declining} title="Decline this shoot" message="Say why; the planner reads it and proposes again." placeholder="Reason" confirmLabel="Decline" multiline
        onConfirm={(v) => { if (v.trim().length < 3) { toast.error('A reason is required'); return; } decide.mutate({ decision: 'declined', note: v.trim() }); }} onCancel={() => setDeclining(false)} />
      <ConfirmDialog open={confirmDelete} title="Delete this shoot?" message="The creatives stay, they just come off the schedule." confirmLabel="Delete" tone="danger"
        onConfirm={() => { setConfirmDelete(false); del.mutate(); }} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
}

function Row({ c, action, reason }: { c: ShootCreative; action?: React.ReactNode; reason?: string }) {
  const st = SCRIPT[c.script_status] ?? SCRIPT.draft;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={`/creatives/${c.id}#shoot`} className="font-mono text-xs text-accent-400 whitespace-nowrap hover:underline">{c.code}</Link>
          <span className="text-muted-strong truncate">{c.title}</span>
          <span className="text-[10px] uppercase text-muted">{c.creative_type}</span>
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', st.cls)}>{reason ?? st.label}</span>
        </div>
        <div className="text-[11px] text-muted truncate">{c.talent_name ? `${c.talent_name}${c.talent_telegram_linked ? '' : ' (not on Telegram)'}` : 'no doctor picked'}{c.videographer_name ? ` · shot by ${c.videographer_name}` : ''}{c.raw_count ? ` · ${c.raw_count} raw${c.raw_count === 1 ? '' : 's'} uploaded` : ''}</div>
      </div>
      {action}
    </div>
  );
}
